"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

const steps = [
    {
        num: '01',
        title: 'Discover',
        desc: 'We analyse your audience, competitors, brand perception, and current social presence.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="12" cy="12" r="7" stroke="#080400" strokeWidth="2.2"/>
                <path d="M17 17L23 23" stroke="#080400" strokeWidth="2.2" strokeLinecap="round"/>
                <path d="M10 12h4M12 10v4" stroke="#080400" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
    },
    {
        num: '02',
        title: 'Define',
        desc: 'We establish content pillars, messaging themes, platform priorities, and audience engagement strategies.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="4" y="6" width="20" height="16" rx="3" stroke="#080400" strokeWidth="2.2"/>
                <path d="M8 11h12M8 15h8" stroke="#080400" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
    },
    {
        num: '03',
        title: 'Create',
        desc: 'We develop content designed to educate, inspire, entertain, and build trust.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M6 22l4-4M14 6l2 2-8 8-2-2 8-8z" stroke="#080400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 4l6 6-2 2-6-6 2-2z" stroke="#080400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        num: '04',
        title: 'Amplify',
        desc: 'We support growth through strategic distribution, paid social campaigns, and audience targeting.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M5 20L11 13l6 4L24 5" stroke="#080400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 5h5v5" stroke="#080400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        num: '05',
        title: 'Optimise',
        desc: 'Using behavioural insights and performance data, we continuously refine content and strategy.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="8" stroke="#080400" strokeWidth="2.2"/>
                <circle cx="14" cy="14" r="3" fill="#080400"/>
                <path d="M14 6v2M14 20v2M6 14h2M20 14h2" stroke="#080400" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
    },
];

export default function SocialApproach() {
    return (
        <section className="relative overflow-hidden py-28 px-6 md:px-16 lg:px-24" style={{ background: '#000000' }}>

            {/* Subtle dot grid */}
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, rgba(206,255,26,0.04) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
            }} />

            {/* Right glow */}
            <div className="absolute pointer-events-none" style={{ width: 600, height: 600, top: '-10%', right: '-10%', background: 'radial-gradient(circle, rgba(206,255,26,0.05) 0%, transparent 65%)', borderRadius: '50%' }} />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-20 relative z-10">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div className="flex flex-col gap-4 items-center text-center md:items-start md:text-left">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            Our Process
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(34px, 4.5vw, 62px)', color: '#fbfdfd', lineHeight: 1.05, letterSpacing: '-0.02em' }}
                        >
                            The Lead Workz<br />Approach
                        </motion.h2>
                    </div>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                    {steps.map((step, i) => (
                        <React.Fragment key={i}>
                            <motion.div
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.55, delay: i * 0.1 }}
                                className={`flex flex-col items-center text-center md:items-start md:text-left gap-5 relative ${i < steps.length - 1 ? 'md:pr-6' : ''}`}
                            >
                                {/* Desktop: horizontal connector line */}
                                {i < steps.length - 1 && (
                                    <motion.div
                                        initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: i * 0.1 + 0.4 }}
                                        className="hidden md:block absolute top-[44px] left-[88px] right-0"
                                        style={{ height: 1, background: 'linear-gradient(90deg, rgba(206,255,26,0.35) 0%, rgba(206,255,26,0.05) 100%)', transformOrigin: 'left' }}
                                    />
                                )}

                                {/* Icon circle */}
                                <div
                                    className="flex items-center justify-center rounded-full shrink-0"
                                    style={{ width: 88, height: 88, background: '#ceff1a', flexShrink: 0, boxShadow: '0 0 40px rgba(206,255,26,0.20)' }}
                                >
                                    {step.icon}
                                </div>

                                {/* Content */}
                                <div className="flex flex-col gap-2 items-center md:items-start">
                                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', color: 'rgba(206,255,26,0.40)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                                        {step.num}
                                    </span>
                                    <h3 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(17px, 1.4vw, 20px)', color: '#fbfdfd', letterSpacing: '0.01em' }}>
                                        {step.title}
                                    </h3>
                                    <p className="text-center md:text-left" style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 13, lineHeight: 1.75, color: 'rgba(251,253,253,0.50)', maxWidth: 220 }}>
                                        {step.desc}
                                    </p>
                                </div>

                                {/* Mobile: vertical connector line between steps */}
                                {i < steps.length - 1 && (
                                    <motion.div
                                        initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: i * 0.1 + 0.3 }}
                                        className="block md:hidden"
                                        style={{ width: 1, height: 40, background: 'linear-gradient(180deg, rgba(206,255,26,0.50) 0%, rgba(206,255,26,0.08) 100%)', transformOrigin: 'top', marginTop: 4, alignSelf: 'center' }}
                                    />
                                )}
                            </motion.div>
                        </React.Fragment>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                >
                    <Link
                        href="/contact"
                        className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(206,255,26,0.25)] text-center w-full md:w-auto"
                        style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 15, letterSpacing: '0.06em' }}
                    >
                        Start With A Social Media Strategy Session
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="#080400" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                </motion.div>

            </div>
        </section>
    );
}
